'use client';

import Logo from '@/components/Logo';
import { useAppContext } from '@/context/app';
import { useHeader } from '@/hooks/Header/useHeader';
import useIsMobile from '@/hooks/use-is-mobile';
import clsx from 'clsx';
import debounce from 'lodash.debounce';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import React, { Suspense, useEffect, useRef } from 'react';
import CartTrigger from './cartTrigger';
import NavTrigger from './navTrigger';
import RecordInfluencer from './record-influencer';
import SearchTrigger from './searchTrigger';

const CurrencySwitcher = dynamic(() => import('./currencySwitcher'), {
  ssr: false,
  loading: () => <div className='h-5 w-[22px] lg_w-[46px]' />,
});

const AccountTrigger = dynamic(() => import('./accountTrigger'), {
  ssr: false,
  loading: () => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='25'
      height='24'
      fill='none'
      viewBox='0 0 25 24'
      className='hidden h-auto lg_block lg_w-5'
    >
      <path
        className='fill-#403732 lg_group-hover_fill-80583F'
        d='M16.068 13.72c2.14-1.24 3.59-3.55 3.59-6.2 0-3.95-3.2-7.16-7.16-7.16a7.166 7.166 0 00-7.15 7.17c0 2.65 1.45 4.96 3.59 6.2a11.679 11.679 0 00-7.97 9.08c-.07.41.2.81.61.88.41.07.81-.2.88-.61a10.2 10.2 0 019.99-8.4h.12c4.92.03 9.11 3.55 9.99 8.4.07.37.39.62.75.62.04 0 .09 0 .14-.01.41-.08.69-.47.61-.88-.8-4.37-3.94-7.8-7.99-9.09zm-9.2-6.19c0-3.11 2.53-5.64 5.64-5.64s5.64 2.53 5.64 5.64c0 3.09-2.49 5.6-5.57 5.63h-.14c-3.08-.04-5.57-2.55-5.57-5.63z'
      />
      <path
        fill='#000'
        fillOpacity='0.2'
        d='M16.068 13.72c2.14-1.24 3.59-3.55 3.59-6.2 0-3.95-3.2-7.16-7.16-7.16a7.166 7.166 0 00-7.15 7.17c0 2.65 1.45 4.96 3.59 6.2a11.679 11.679 0 00-7.97 9.08c-.07.41.2.81.61.88.41.07.81-.2.88-.61a10.2 10.2 0 019.99-8.4h.12c4.92.03 9.11 3.55 9.99 8.4.07.37.39.62.75.62.04 0 .09 0 .14-.01.41-.08.69-.47.61-.88-.8-4.37-3.94-7.8-7.99-9.09zm-9.2-6.19c0-3.11 2.53-5.64 5.64-5.64s5.64 2.53 5.64 5.64c0 3.09-2.49 5.6-5.57 5.63h-.14c-3.08-.04-5.57-2.55-5.57-5.63z'
      />
    </svg>
  ),
});

const SearchBar = React.lazy(() => import('@/components/SearchBar'));

const HeaderToolbar = props => {
  const { miniCartOpen, setMiniCartOpen, setShowNoItemDrawer, announcements, setShouldTranslateHeader } = props;

  const isMobile = useIsMobile();
  const { handleSearchTriggerClick, searchTriggerRef, isSearchOpen, setIsSearchOpen, searchRef } = useHeader();
  const headerToolbarRef = useRef<HTMLDivElement>(null);

  const [
    ,
    {
      actions: { toggleHeadStickedOnTop, setHeaderToolbarHeight },
    },
  ] = useAppContext();

  useEffect(() => {
    const headerToolBarElement = headerToolbarRef.current;

    if (!headerToolBarElement) return;

    setHeaderToolbarHeight(headerToolBarElement.offsetHeight || 0);

    const resizeHandler = debounce(() => {
      setHeaderToolbarHeight(headerToolBarElement.offsetHeight || 0);
    }, 500);

    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [setHeaderToolbarHeight]);

  // 添加滚动监听
  useEffect(() => {
    if (isMobile) {
      setShouldTranslateHeader?.(false);
      return;
    }

    let lastScrollTop;
    let direction;
    let flag = true;

    const addToCartHandler = () => {
      setShouldTranslateHeader?.(false);
    };

    document.addEventListener('CART_ADD_ITEM', addToCartHandler);

    document.body.onscroll = function () {
      if (document.body.style.overflow === 'hidden') {
        return;
      }

      if (!flag) {
        return;
      }

      const st = document.documentElement.scrollTop; // Credits: "https://github.com/qeremy/so/blob/master/so.dom.js#L426"

      if (st > lastScrollTop) {
        // downscroll code
        direction = 'down';
      } else if (st < lastScrollTop) {
        // upscroll code
        direction = 'up';
      } // else was horizontal scroll

      lastScrollTop = st <= 0 ? 0 : st; // For Mobile or

      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      setShouldTranslateHeader?.(scrollTop > 30 && direction === 'down');
      toggleHeadStickedOnTop(scrollTop > 30 && direction === 'down');

      flag = false;

      setTimeout(function () {
        flag = true;
      }, 100);
    };

    return () => {
      document.removeEventListener('CART_ADD_ITEM', addToCartHandler);
    };
  }, [isMobile, toggleHeadStickedOnTop, setShouldTranslateHeader]);

  return (
    <div
      className={clsx(
        'after_pointer-events-none after_absolute after_bottom-0 after_left-0 after_h-[1px] after_w-full after_bg-[#eee]',
        isSearchOpen ? 'lg_after_opacity-0' : '',
      )}
      ref={headerToolbarRef}
    >
      <Suspense fallback={null}>
        <RecordInfluencer />
      </Suspense>
      {announcements}
      <div className='bg-[#fff4e9] py-2 text-center text-[15px] leading-5 lg_py-3 lg_text-[17px]'>
        AW.Bridal 10th anniversary,&nbsp;
        <br className='lg_hidden' />
        sweet&nbsp;<span className='font-semibold'>SURPRISES</span>&nbsp;in July! Stay tuned!
      </div>
      <div>
        <div className='mx-auto flex h-[60px] items-center justify-between px-[15px] lg_h-auto lg_w-[calc(100%_-_80px)] lg_px-0 lg_py-5'>
          {isMobile ? (
            <div className='flex flex-1 basis-1/2 items-center justify-start gap-x-[18px]'>
              <NavTrigger navigationRef={props.navigationRef} />
              <SearchTrigger
                onClick={handleSearchTriggerClick}
                isSearchOpen={isSearchOpen}
                ref={searchTriggerRef}
              />
            </div>
          ) : (
            <div className='flex flex-1 basis-1/2 items-center justify-start gap-x-5'>
              <CurrencySwitcher />
              <Link
                href='/swatches'
                className='group flex items-center gap-x-1.5'
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='19'
                  height='23'
                  fill='none'
                  viewBox='0 0 19 23'
                >
                  <g
                    className='fill-#403732 lg_group-hover_fill-80583F'
                    clipPath='url(#clip0_12101_8630)'
                  >
                    <path d='M16.328 22.032H1.878A1.879 1.879 0 010 20.156V5.867h18.207v14.29a1.879 1.879 0 01-1.879 1.875zM1.058 6.919v13.234c0 .454.37.82.82.82h14.447c.454 0 .821-.37.821-.82V6.92H1.057z' />
                    <path d='M18.207 6.919H0V1.907C0 .872.842.031 1.878.031h14.447c1.036 0 1.878.841 1.878 1.876V6.92h.004zM1.057 5.863H17.15V1.907c0-.454-.37-.82-.822-.82H1.878a.824.824 0 00-.82.82v3.956z' />
                    <path d='M15.114 5.86H3.074v1.055h12.04V5.86z' />
                    <path d='M3.125 19.3h-.017c-.18-.003-.444-.07-.688-.366-.518-.626-.12-1.281.141-1.71.166-.271.339-.553.339-.81 0-.27-.088-.384-.247-.584-.2-.254-.476-.598-.476-1.239 0-.64.275-.985.476-1.239.159-.2.247-.31.247-.584 0-.275-.088-.384-.247-.584-.2-.254-.476-.598-.476-1.239 0-.64.275-.985.476-1.239.159-.2.247-.31.247-.58 0-.272-.088-.384-.247-.585-.2-.253-.476-.598-.476-1.239 0-.64.275-.985.476-1.238l.159-.201.257-.004h12.296l.159.201c.2.253.476.598.476 1.239 0 .64-.275.985-.476 1.239-.159.2-.247.31-.247.584 0 .27.088.384.247.584.2.254.476.598.476 1.239 0 .64-.275.985-.476 1.239-.159.2-.247.31-.247.584 0 .271.088.384.247.584.2.254.476.599.476 1.24 0 .608-.29.992-.5 1.27-.205.27-.268.366-.22.58.085.387.255.651.417.908.236.373.599.94.063 1.588-.243.295-.507.362-.687.366h-.018c-.433 0-.73-.331-.99-.623-.116-.13-.363-.405-.462-.405-.098 0-.148.042-.335.282-.211.277-.535.693-1.17.693-.634 0-.958-.419-1.17-.694-.187-.239-.236-.281-.335-.281-.098 0-.148.042-.334.282-.212.277-.536.693-1.17.693-.635 0-.96-.419-1.17-.694-.184-.239-.237-.281-.332-.281-.099 0-.148.042-.335.282-.211.277-.535.693-1.17.693-.638 0-.958-.419-1.173-.694-.187-.239-.236-.281-.335-.281-.099 0-.342.274-.462.405-.257.288-.553.62-.987.62zm7.472-2.083c.638 0 .959.418 1.17.693.183.24.236.282.335.282.099 0 .148-.043.335-.282.211-.278.535-.693 1.17-.693.546 0 .93.4 1.212.714-.003-.007-.01-.014-.014-.021-.183-.285-.43-.68-.557-1.25-.151-.696.173-1.126.409-1.439.187-.25.286-.39.286-.633 0-.271-.089-.384-.247-.585-.201-.253-.476-.598-.476-1.239 0-.64.275-.985.476-1.238.158-.2.247-.31.247-.585 0-.27-.089-.383-.247-.584-.201-.253-.476-.598-.476-1.239 0-.64.275-.985.476-1.238.158-.201.247-.31.247-.585a.666.666 0 00-.1-.38H3.338a.664.664 0 00-.102.387c0 .271.088.384.246.585.201.253.476.598.476 1.238 0 .641-.275.986-.476 1.24-.158.2-.246.309-.246.583 0 .271.088.384.246.585.201.253.476.598.476 1.239 0 .64-.275.985-.476 1.238-.158.201-.246.31-.246.585 0 .274.088.383.246.584.201.253.476.598.476 1.239 0 .552-.275 1-.493 1.358-.025.042-.057.088-.085.141.282-.31.66-.693 1.195-.693.638 0 .958.418 1.173.693.187.24.237.281.335.281.099 0 .148-.042.335-.281.212-.278.536-.693 1.17-.693.635 0 .959.418 1.17.693.184.24.236.281.332.281.098 0 .148-.042.334-.281.215-.282.536-.7 1.174-.7zM15.508 2.25H8.48v1.056h7.028V2.25zM15.506 4.14h-3.514v1.056h3.514V4.141z' />
                  </g>
                  <defs>
                    <clipPath id='clip0_12101_8630'>
                      <path
                        fill='#fff'
                        d='M0 0H18.207V22H0z'
                        transform='translate(0 .031)'
                      />
                    </clipPath>
                  </defs>
                </svg>
                <span className='text-sm leading-[19px] text-#403732 lg_group-hover_text-80583F'>
                  Get 5 Free Swatches
                </span>
              </Link>
            </div>
          )}

          <Link
            aria-label='Aw bridal'
            href='/'
            className='block flex-none'
            data-cy='Header-logoContainer'
          >
            <Logo />
          </Link>
          <div className='flex flex-1 basis-1/2 items-center justify-end gap-x-[18px] lg_gap-x-5 '>
            {!isMobile ? (
              <SearchTrigger
                onClick={handleSearchTriggerClick}
                isSearchOpen={isSearchOpen}
                ref={searchTriggerRef}
              />
            ) : null}
            <AccountTrigger />
            {isMobile && <CurrencySwitcher />}
            <CartTrigger
              miniCartOpen={miniCartOpen}
              setMiniCartOpen={setMiniCartOpen}
              setShowNoItemDrawer={setShowNoItemDrawer}
            />
          </div>
        </div>
      </div>
      {isSearchOpen ? (
        <Suspense
          fallback={
            <div
              className={clsx(
                ' h-[50px] w-full ',
                'absolute left-0 top-full ',
                'bg-white',
                'flex items-center justify-center gap-x-4',
              )}
            >
              <div
                className={clsx(
                  ' h-3 w-3 ',
                  'rounded-[50%] bg-E7DED5',
                  'animate-[loading-pulse_.6s_alternate_infinite]',
                )}
              />
              <div
                className={clsx(
                  ' h-3 w-3 ',
                  'rounded-[50%] bg-E7DED5',
                  'animate-[loading-pulse_.6s_.2s_alternate_infinite]',
                )}
              />
              <div
                className={clsx(
                  ' h-3 w-3 ',
                  'rounded-[50%] bg-E7DED5',
                  'animate-[loading-pulse_.6s_.4s_alternate_infinite]',
                )}
              />
            </div>
          }
        >
          <SearchBar
            isOpen={isSearchOpen}
            setIsSearchOpen={setIsSearchOpen}
            ref={searchRef}
            hasAnnouncements={Array.isArray(props.announcementsData) && props.announcementsData.length > 0}
          />
        </Suspense>
      ) : null}
    </div>
  );
};

export default HeaderToolbar;
